import { ExportOutlined } from '@ant-design/icons';
import { Card, Divider, Flex, Modal, Space, Table, Typography } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchInput from '../../../components/ui/SearchInput';
import { getOrderLink } from '../../../redux/actions/order';
import { getProductActivityDetails } from '../../../redux/actions/products';
import { getRoutes } from '../../../routes/Routes';
import { formatCount, formatCurrency } from '../../../utils';
import MarginTag from '../../../components/ui/MarginTag';

const searchableFields = [
    'customer_code',
    'code',
    'customer_name',
    'order_code',
    'sub_category',
    'added_by',
];

const ActivityDetail = ({ filter, open, onClose }) => {
    const [allActivityData, setAllActivityData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchData = async (filters) => {
        setLoading(true);

        const res = await getProductActivityDetails(filters);
        const data = res?.product || [];

        setAllActivityData(data);

        setLoading(false);
    };

    const handleNetsuitePDF = async (internal_id) => {
        if (!internal_id) return;
        const res = await getOrderLink(internal_id);
        if (res?.ack && res.link) {
            window.open(res.link, '_blank', 'noopener,noreferrer');
        }
    };

    const activityData = useMemo(() => {
        if (!searchText?.trim()) {
            return allActivityData;
        }

        const searchValue = _.toLower(_.trim(searchText));

        return _.filter(allActivityData, (item) =>
            _.some(searchableFields, (field) =>
                _.includes(
                    _.toLower(_.toString(item?.[field])),
                    searchValue
                )
            )
        );
    }, [allActivityData, searchText]);

    const columns = [
        {
            title: 'Order Date',
            dataIndex: 'order_date',
            key: 'order_date',
            align: 'center',
            render: (text) => text ? dayjs(text).format('DD-MM-YYYY') : '-',
        },
        {
            title: 'Customer',
            dataIndex: 'customer_name',
            key: 'customer_name',
            render: (text, record) => (
                <div className="flex align-items-center gap-1 min-w-0" style={{ maxWidth: 300 }}>
                    <Typography.Text
                        className="min-w-0"
                        ellipsis={{
                            tooltip: `${record?.customer_code} - ${text}`,
                        }}
                    >
                        {record?.customer_code} - {text}
                    </Typography.Text>

                    <Link
                        to={getRoutes().customer_details.route(
                            record?.customer_netsuite_id
                        )}
                    >
                        <ExportOutlined />
                    </Link>
                </div>
            ),
        },
        {
            title: 'Internal Code',
            dataIndex: 'code',
            key: 'code',
            render: (_, record) => {
                return (
                    <div className="flex align-items-center gap-1">
                        {record.code}
                        {record.id && (
                            <Typography.Link href={getRoutes().invoice_details.route(record?.id)}>
                                <ExportOutlined />
                            </Typography.Link>
                        )}
                    </div>
                )
            },
        },
        {
            title: 'Invoice Code',
            dataIndex: 'order_code',
            key: 'order_code',
            render: (_, record) => {
                return (
                    <div className="flex align-items-center gap-1">
                        {record.order_code}
                        {record.internal_id && (
                            <Typography.Link>
                                <ExportOutlined onClick={() => handleNetsuitePDF(record?.internal_id)} />
                            </Typography.Link>
                        )}
                    </div>
                )
            },
        },
        {
            title: 'Order Type',
            dataIndex: 'type',
            key: 'type',
            render: (value) => {
                if (value === 1) return 'Collection';
                if (value === 2) return 'Delivery';
                return '-';
            },
        },
        {
            title: 'Price',
            dataIndex: 'unit_price',
            key: 'unit_price',
            align: 'center',
            render: (text) => formatCurrency(text),
        },
        {
            title: 'Price Level',
            dataIndex: 'price_level',
            key: 'price_level',
            align: 'center',
        },
        {
            title: 'Margin %',
            dataIndex: 'margin',
            key: 'margin',
            align: 'center',
            render: (text) => <MarginTag margin={Number(text || 0)} />
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'center',
            render: (text) => formatCount(text),
        },
        {
            title: 'Created By',
            dataIndex: 'added_by',
            key: 'added_by',
        }
    ]

    useEffect(() => {
        if (filter) {
            fetchData(filter);
        }
    }, [filter]);

    return (
        <Modal
            title="Product Activity Detail"
            open={open}
            onCancel={onClose}
            centered
            width="80vw"
            footer={null}
        >
            <Card size="small" className="mb-3 shadow-sm" variant="borderless">
                <Flex justify='space-between' wrap gap={16} align='center'>
                    <div className="lg:max-w-20rem w-full">
                        <SearchInput
                            size="large"
                            placeholder="Search..."
                            onSearch={setSearchText}
                            allowClear
                        />
                    </div>

                    <Space size={24} wrap split="|">
                        <Space direction="vertical" size={0}>
                            <Typography.Text type="secondary">
                                Product
                            </Typography.Text>

                            <Typography.Text strong>
                                {filter?.product_code} - {filter?.product_name}
                            </Typography.Text>
                        </Space>

                        <Space direction="vertical" size={0}>
                            <Typography.Text type="secondary">
                                Date Range
                            </Typography.Text>

                            <Typography.Text strong>
                                {filter?.start_date && filter?.end_date
                                    ? `${dayjs(filter.start_date).format('DD-MM-YYYY')} - ${dayjs(filter.end_date).format('DD-MM-YYYY')}`
                                    : 'N/A'}
                            </Typography.Text>
                        </Space>

                        <Space direction="vertical" size={0}>
                            <Typography.Text type="secondary">
                                Available Quantity
                            </Typography.Text>

                            <Typography.Text strong>
                                {formatCount(filter?.available_quantity)}
                            </Typography.Text>
                        </Space>

                        <Space direction="vertical" size={0}>
                            <Typography.Text type="secondary">
                                OnHand Quantity
                            </Typography.Text>

                            <Typography.Text strong>
                                {formatCount(filter?.on_hand_quantity)}
                            </Typography.Text>
                        </Space>
                    </Space>
                </Flex>
            </Card>

            <Divider className="my-3" />

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.id}
                dataSource={activityData}
                pagination={false}
                loading={loading}
                scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
                components={{
                    body: {
                        cell: ({ className, ...rest }) => (
                            <td
                                {...rest}
                                className={
                                    activityData?.length
                                        ? clsx(className, 'py-1')
                                        : className
                                }
                            />
                        ),
                    },
                }}
            />

        </Modal>
    )
}

export default ActivityDetail